// /src/pages/CreateCompany.jsx
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { motion } from "framer-motion";
import { useForm } from "react-hook-form";
import { Link, useNavigate } from "react-router-dom";
import { FaArrowLeft } from "react-icons/fa";
import toast from "react-hot-toast";
import { useAddCompanyMutation ,useGetAllCompaniesQuery } from "../toolkitServices/companyApi";
import { indianStates } from "../utils/helpers";

/* ---------- Shared UI tokens (consistent with CreateCategory) ---------- */
const baseInput =
  "w-full px-3 py-2.5 border rounded-lg text-[14px] focus:outline-none";
const okRing =
  "border-gray-300 focus:ring-4 focus:ring-[#9481ce]/20 focus:border-[#9481ce]";
const errRing =
  "border-red-400 focus:ring-4 focus:ring-red-200 focus:border-red-500";

const CreateCompany = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);

  const {
    register,
    handleSubmit,
    formState: { errors },
    trigger,
    reset,
    watch,
    setFocus,
  } = useForm({
    defaultValues: {
      companyName: "",
      shortName: "",
      owner: "",
      contact: "",
      email: "",

      // ✅ NEW: employee email initial
      employeeEmailInitial: "",

      registrationNumber: "",
      establishmentYear: "",
      adhar: "",
      pan: "",
      cgstNumber: "",
      gstNumber: "",
      vatNumber: "",
      addressLine1: "",
      city: "",
      pincode: "",
      state: "Maharashtra",
      country: "india",
    },
  });

  const [addCompany, { isLoading, error, isSuccess, data }] =
    useAddCompanyMutation();

    const {
  data: companyListData,
  isLoading: isCheckingCompanies,
  isFetching: isCheckingCompaniesFetching,
} = useGetAllCompaniesQuery(
  { page: 1, limit: 1, search: "" },
  {
    refetchOnMountOrArgChange: true,
    refetchOnFocus: false,
    refetchOnReconnect: false,
  }
);

const existingCompanies = companyListData?.data || [];
const hasExistingCompany = existingCompanies.length > 0;


  // ✅ NEW: preview email string
  const employeeEmailInitial = watch("employeeEmailInitial");
  const employeePreview = useMemo(() => {
    const clean = (employeeEmailInitial || "")
      .trim()
      .toLowerCase()
      .replace(/\s+/g, "")
      .replace(/[^a-z0-9._-]/g, "");
    return `employee@${clean || "___"}.com`;
  }, [employeeEmailInitial]);


  useEffect(() => {
  if (
    !isCheckingCompanies &&
    !isCheckingCompaniesFetching &&
    hasExistingCompany
  ) {
    toast.error("Company already exists.");
    navigate("/organisation/company-master-list", { replace: true });
  }
}, [
  hasExistingCompany,
  isCheckingCompanies,
  isCheckingCompaniesFetching,
  navigate,
]);

  // Success -> stay on page, reset & refocus first field
  useEffect(() => {
    if (isSuccess && (data?.success ?? true)) {
      toast.success("Company added!");
      reset({
        companyName: "",
        shortName: "",
        owner: "",
        contact: "",
        email: "",

        // ✅ NEW
        employeeEmailInitial: "",

        registrationNumber: "",
        establishmentYear: "",
        adhar: "",
        pan: "",
        cgstNumber: "",
        gstNumber: "",
        vatNumber: "",
        addressLine1: "",
        city: "",
        pincode: "",
        state: "Maharashtra",
        country: "india",
      });
      setStep(1);
      requestAnimationFrame(() =>
        setFocus("companyName", { shouldSelect: true })
      );
      navigate(-1)
    }
  }, [isSuccess, data, reset, setFocus]);

  // Error toast
  useEffect(() => {
    if (error) {
      const msg =
        error?.data?.msg ||
        error?.data?.message ||
        error?.error ||
        "Error adding company!";
      toast.error(msg);
    }
  }, [error]);

  // Keyboard shortcuts: Ctrl/⌘+S save/next, Ctrl/⌘+Backspace back, Alt+← prev step
  const nextStep = useCallback(async () => {
    const valid = await trigger();
    if (valid) setStep((s) => (s < 2 ? s + 1 : s));
  }, [trigger]);

  const prevStep = useCallback(() => {
    setStep((s) => (s > 1 ? s - 1 : s));
  }, []);

  useEffect(() => {
    const handler = (e) => {
      const isSave = (e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "s";
      const isBack = (e.ctrlKey || e.metaKey) && e.key === "Backspace";
      const isPrev = e.altKey && e.key === "ArrowLeft";

      if (isSave) {
        e.preventDefault();
        if (step === 2) {
          handleSubmit(onSubmit)();
        } else {
          nextStep();
        }
      }
      if (isBack) {
        e.preventDefault();
        navigate(-1);
      }
      if (isPrev) {
        e.preventDefault();
        prevStep();
      }
    };

    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [step, handleSubmit, navigate, nextStep, prevStep]);

  // Auto-focus per step (animation-safe)
  useEffect(() => {
    const raf = requestAnimationFrame(() => {
      if (step === 1) setFocus("companyName", { shouldSelect: true });
      if (step === 2) setFocus("addressLine1", { shouldSelect: true });
    });
    return () => cancelAnimationFrame(raf);
  }, [step, setFocus]);

  const onSubmit = async (form) => {
    const payload = {
      companyName: (form.companyName || "").trim(),
      shortName: (form.shortName || "").trim(),
      owner: (form.owner || "").trim(),
      contact: (form.contact || "").trim(),
      email: (form.email || "").trim(),

      // ✅ NEW
      employeeEmailInitial: (form.employeeEmailInitial || "").trim(),

      registrationNumber: (form.registrationNumber || "").trim(),
      establishmentYear: form.establishmentYear || "",
      adhar: (form.adhar || "").trim(),
      pan: (form.pan || "").trim(),
      cgstNumber: (form.cgstNumber || "").trim(),
      gstNumber: (form.gstNumber || "").trim(),
      vatNumber: (form.vatNumber || "").trim(),
      addressLine1: (form.addressLine1 || "").trim(),
      city: (form.city || "").trim(),
      pincode: (form.pincode || "").trim(),
      state: form.state,
      country: form.country,
    };

    try {
      await addCompany(payload).unwrap();
      // success handled in isSuccess effect
    } catch {
      // toast handled in error effect
    }
  };

  const loading = isLoading;


  if (isCheckingCompanies || isCheckingCompaniesFetching) {
  return (
    <div className="px-4 py-4">
      <div className="max-w-5xl mx-auto bg-white rounded-xl border border-gray-200 p-6 flex items-center justify-center">
        <div className="animate-spin h-6 w-6 border-2 border-[#9481ce] border-t-transparent rounded-full" />
      </div>
    </div>
  );
}

if (hasExistingCompany) return null;

  return (
    <div className="px-4 py-4">
      {/* Compact Header (same style as CreateCategory) */}
      <div className="max-w-5xl mx-auto">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 px-2 py-2 flex items-center gap-3">
          <Link
            to={"/organisation/company-master-list"}
            title="Back to Company List"
            className="inline-flex items-center justify-center h-9 w-9 rounded-full border border-gray-200 hover:bg-gray-100 transition"
          >
            <FaArrowLeft className="text-[#6f5cc5] text-sm" />
          </Link>
          <div className="flex-1">
            <div className="flex items-center justify-between">
              <h1 className="text-xl md:text-2xl font-semibold text-[#4b3d91] leading-tight">
                Create Company
              </h1>
              <span className="text-xs text-gray-500 mr-2">
                Step {step} of 2
              </span>
            </div>
            <p className="text-xs text-gray-500">
              Tips:{" "}
              <kbd className="px-1 bg-gray-100 rounded border">Ctrl</kbd>+
              <kbd className="px-1 bg-gray-100 rounded border">S</kbd> to
              save/next,{" "}
              <kbd className="px-1 bg-gray-100 rounded border ml-2">Ctrl</kbd>+
              <kbd className="px-1 bg-gray-100 rounded border">Back Spc</kbd>{" "}
              go back,{" "}
              <kbd className="px-1 bg-gray-100 rounded border ml-2">Alt</kbd>+
              <kbd className="px-1 bg-gray-100 rounded border">←</kbd> prev step
            </p>
          </div>
        </div>
      </div>

      {/* Card — compact; BORDER-ONLY validation; loading overlay */}
      <motion.div
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.28 }}
        className="max-w-5xl mx-auto bg-white shadow-md rounded-xl mt-4 border border-gray-200 relative"
        onAnimationComplete={() =>
          requestAnimationFrame(() =>
            setFocus(step === 1 ? "companyName" : "addressLine1")
          )
        }
      >
        {/* Loading overlay */}
        {loading && (
          <div className="absolute inset-0 bg-white/60 backdrop-blur-[2px] flex items-center justify-center z-20 rounded-xl">
            <div className="animate-spin h-7 w-7 border-2 border-[#9481ce] border-t-transparent rounded-full" />
          </div>
        )}

        <form className="p-5" onSubmit={(e) => e.preventDefault()}>
          {/* STEP 1 */}
          {step === 1 && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Company Name */}
              <div>
                <label className="block text-[13px] font-medium text-gray-700 mb-1">
                  Company Name <span className="text-red-500">*</span>
                </label>
                <input
                  {...register("companyName", {
                    required: "Company name is required",
                  })}
                  aria-invalid={!!errors.companyName}
                  placeholder="Enter company name"
                  className={`${baseInput} ${
                    errors.companyName ? errRing : okRing
                  }`}
                />
              </div>

              {/* Short Name */}
              <div>
                <label className="block text-[13px] font-medium text-gray-700 mb-1">
                  Company Short Name <span className="text-red-500">*</span>
                </label>
                <input
                  {...register("shortName", {
                    required: "Short name is required",
                  })}
                  aria-invalid={!!errors.shortName}
                  placeholder="Enter short name"
                  className={`${baseInput} ${
                    errors.shortName ? errRing : okRing
                  }`}
                />
              </div>

              {/* Owner */}
              <div>
                <label className="block text-[13px] font-medium text-gray-700 mb-1">
                  Owner
                </label>
                <input
                  {...register("owner")}
                  placeholder="Enter owner"
                  className={`${baseInput} ${okRing}`}
                />
              </div>

              {/* Contact */}
              <div>
                <label className="block text-[13px] font-medium text-gray-700 mb-1">
                  Contact <span className="text-red-500">*</span>
                </label>
                <input
                  {...register("contact", {
                    required: "Contact is required",
                    validate: (v) =>
                      /^\d{10}$/.test(v) ||
                      "Contact must be exactly 10 digits",
                  })}
                  aria-invalid={!!errors.contact}
                  placeholder="Enter contact"
                  className={`${baseInput} ${
                    errors.contact ? errRing : okRing
                  }`}
                />
                {errors.contact?.message && (
                  <p className="text-xs text-red-600 mt-1">
                    {errors.contact.message}
                  </p>
                )}
              </div>

              {/* ✅ NEW: Employee Email Initial (Above Email) */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-[13px] font-medium text-gray-700">
                    Employee Email Initial{" "}
                    <span className="text-red-500">*</span>
                  </label>

                  <span className="text-[11px] text-gray-500">
                    {/* Example:{" "} */}
                    <span className="font-semibold text-gray-700 text-[9px]">
                      {employeePreview}
                    </span>
                  </span>
                </div>

                <input
                  {...register("employeeEmailInitial", {
                    required: "Employee email initial is required",
                    validate: (v) =>
                      /^[a-zA-Z0-9._-]{2,30}$/.test((v || "").trim()) ||
                      "Use 2-30 chars: a-z, 0-9, dot, underscore, dash",
                  })}
                  aria-invalid={!!errors.employeeEmailInitial}
                  placeholder="e.g. loyalstring / sparkle / motiwala"
                  className={`${baseInput} ${
                    errors.employeeEmailInitial ? errRing : okRing
                  }`}
                />

                {errors.employeeEmailInitial?.message && (
                  <p className="text-xs text-red-600 mt-1">
                    {errors.employeeEmailInitial.message}
                  </p>
                )}
              </div>

              {/* Email */}
              <div>
                <label className="block text-[13px] font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  {...register("email", {
                    validate: (v) =>
                      !v ||
                      /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) ||
                      "Invalid email address",
                  })}
                  aria-invalid={!!errors.email}
                  placeholder="Enter email"
                  className={`${baseInput} ${errors.email ? errRing : okRing}`}
                />
                {errors.email?.message && (
                  <p className="text-xs text-red-600 mt-1">
                    {errors.email.message}
                  </p>
                )}
              </div>

              {/* Registration Number */}
              <div>
                <label className="block text-[13px] font-medium text-gray-700 mb-1">
                  Registration Number
                </label>
                <input
                  {...register("registrationNumber")}
                  placeholder="Enter Registration Number"
                  className={`${baseInput} ${okRing}`}
                />
              </div>

              {/* Establishment Year */}
              <div>
                <label className="block text-[13px] font-medium text-gray-700 mb-1">
                  Establishment Year
                </label>
                <input
                  type="date"
                  {...register("establishmentYear")}
                  className={`${baseInput} ${okRing}`}
                />
              </div>

              {/* Aadhar */}
              <div>
                <label className="block text-[13px] font-medium text-gray-700 mb-1">
                  Adhar
                </label>
                <input
                  {...register("adhar", {
                    validate: (v) =>
                      v === "" ||
                      /^\d{12}$/.test(v) ||
                      "Aadhar must be a 12-digit number",
                  })}
                  aria-invalid={!!errors.adhar}
                  placeholder="Enter adhar"
                  className={`${baseInput} ${errors.adhar ? errRing : okRing}`}
                />
                {errors.adhar?.message && (
                  <p className="text-xs text-red-600 mt-1">
                    {errors.adhar.message}
                  </p>
                )}
              </div>
<div className="md:col-span-3 grid grid-cols-1 md:grid-cols-4 gap-4">
              {/* PAN */}
              <div>
                <label className="block text-[13px] font-medium text-gray-700 mb-1">
                  PAN
                </label>
                <input
                  {...register("pan", {
                    validate: (v) =>
                      v === "" ||
                      /^[A-Z]{5}[0-9]{4}[A-Z]$/.test(
                        (v || "").toUpperCase()
                      ) ||
                      "PAN must be in format AAAAA9999A",
                  })}
                  aria-invalid={!!errors.pan}
                  placeholder="Enter pan"
                  className={`${baseInput} ${errors.pan ? errRing : okRing}`}
                />
                {errors.pan?.message && (
                  <p className="text-xs text-red-600 mt-1">
                    {errors.pan.message}
                  </p>
                )}
              </div>

             
              {/* CGST */}
              <div>
                <label className="block text-[13px] font-medium text-gray-700 mb-1">
                  CGST Number
                </label>
                <input
                  {...register("cgstNumber", {
                    validate: (v) =>
                      v === "" ||
                      /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(
                        (v || "").toUpperCase()
                      ) ||
                      "Invalid CGST format (e.g. 27ABCDE1234F1Z5)",
                  })}
                  aria-invalid={!!errors.cgstNumber}
                  placeholder="Enter CGST Number"
                  className={`${baseInput} ${
                    errors.cgstNumber ? errRing : okRing
                  }`}
                />
                {errors.cgstNumber?.message && (
                  <p className="text-xs text-red-600 mt-1">
                    {errors.cgstNumber.message}
                  </p>
                )}
              </div>

              {/* GSTIN */}
              <div>
                <label className="block text-[13px] font-medium text-gray-700 mb-1">
                  GSTIN Number
                </label>
                <input
                  {...register("gstNumber", {
                    validate: (v) =>
                      v === "" ||
                      /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(
                        (v || "").toUpperCase()
                      ) ||
                      "Invalid GSTIN format (e.g. 27ABCDE1234F1Z5)",
                  })}
                  aria-invalid={!!errors.gstNumber}
                  placeholder="Enter GSTIN Number"
                  className={`${baseInput} ${
                    errors.gstNumber ? errRing : okRing
                  }`}
                />
                {errors.gstNumber?.message && (
                  <p className="text-xs text-red-600 mt-1">
                    {errors.gstNumber.message}
                  </p>
                )}
              </div>

              {/* VAT */}
              <div>
                <label className="block text-[13px] font-medium text-gray-700 mb-1">
                  VAT Number
                </label>
                <input
                  {...register("vatNumber", {
                    validate: (v) =>
                      v === "" ||
                      /^[0-9a-zA-Z\-]{5,20}$/.test(v || "") ||
                      "Invalid VAT Number format",
                  })}
                  aria-invalid={!!errors.vatNumber}
                  placeholder="Enter VAT Number"
                  className={`${baseInput} ${
                    errors.vatNumber ? errRing : okRing
                  }`}
                />
                {errors.vatNumber?.message && (
                  <p className="text-xs text-red-600 mt-1">
                    {errors.vatNumber.message}
                  </p>
                )}
              </div>
              </div>
            </div>
          )}

          {/* STEP 2 */}
          {step === 2 && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Address Line 1 */}
              <div className="md:col-span-3">
                <label className="block text-[13px] font-medium text-gray-700 mb-1">
                  Address Line 1
                </label>
                <input
                  {...register("addressLine1")}
                  placeholder="Enter street / area"
                  className={`${baseInput} ${okRing}`}
                />
              </div>

              {/* City */}
              <div>
                <label className="block text-[13px] font-medium text-gray-700 mb-1">
                  City <span className="text-red-500">*</span>
                </label>
                <input
                  {...register("city", { required: "City is required" })}
                  aria-invalid={!!errors.city}
                  placeholder="Enter city"
                  className={`${baseInput} ${errors.city ? errRing : okRing}`}
                />
                {errors.city?.message && (
                  <p className="text-xs text-red-600 mt-1">
                    {errors.city.message}
                  </p>
                )}
              </div>

              {/* Pincode */}
              <div>
                <label className="block text-[13px] font-medium text-gray-700 mb-1">
                  Pincode <span className="text-red-500">*</span>
                </label>
                <input
                  {...register("pincode", {
                    required: "Pincode is required",
                    validate: (v) =>
                      v === "" ||
                      /^\d{6}$/.test(v) ||
                      "Pincode must be a 6-digit number",
                  })}
                  aria-invalid={!!errors.pincode}
                  placeholder="Enter pincode"
                  className={`${baseInput} ${
                    errors.pincode ? errRing : okRing
                  }`}
                />
                {errors.pincode?.message && (
                  <p className="text-xs text-red-600 mt-1">
                    {errors.pincode.message}
                  </p>
                )}
              </div>

              {/* State */}
              <div>
                <label className="block text-[13px] font-medium text-gray-700 mb-1">
                  State <span className="text-red-500">*</span>
                </label>
                <select
                  {...register("state", { required: "State is required" })}
                  aria-invalid={!!errors.state}
                  className={`${baseInput} ${errors.state ? errRing : okRing}`}
                >
 <option value="">Select State</option>

                  {indianStates.map((state) => (
                    <option key={state} value={state}>
                      {state}
                    </option>
                  ))}
                </select>
              </div>

              {/* Country */}
              <div>
                <label className="block text-[13px] font-medium text-gray-700 mb-1">
                  Country <span className="text-red-500">*</span>
                </label>
                <select
                  {...register("country", { required: "Country is required" })}
                  aria-invalid={!!errors.country}
                  className={`${baseInput} ${
                    errors.country ? errRing : okRing
                  }`}
                >
                  <option value={"india"}>India</option>
                </select>
              </div>
            </div>
          )}

          {/* Footer actions */}
          <div className="flex flex-col sm:flex-row gap-2 pt-5">
            <motion.button
              whileHover={{ scale: isLoading ? 1 : 1.01 }}
              whileTap={{ scale: isLoading ? 1 : 0.98 }}
              type="button"
              onClick={prevStep}
              className="inline-flex justify-center items-center w-full sm:w-auto px-4 py-2.5 rounded-lg font-medium border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 disabled:opacity-60"
              disabled={step === 1 || isLoading}
              title="Previous step"
            >
              Previous
            </motion.button>

            <motion.button
              whileHover={{ scale: isLoading ? 1 : 1.01 }}
              whileTap={{ scale: isLoading ? 1 : 0.98 }}
              type="button"
              onClick={(e) => {
                e.preventDefault();
                if (step === 2) handleSubmit(onSubmit)();
                else nextStep();
              }}
              className="inline-flex justify-center items-center w-full sm:w-auto px-4 py-2.5 rounded-lg font-medium shadow-sm bg-[#9481ce] hover:bg-[#7d6ec0] text-white disabled:opacity-60"
              disabled={isLoading}
              title={step === 2 ? "Submit (Ctrl+S)" : "Next (Ctrl+S)"}
            >
              {isLoading ? "Saving..." : step === 2 ? "Submit (Ctrl+S)" : "Next"}
            </motion.button>

            <Link
              to="/organisation/company-master-list"
              className="inline-flex justify-center items-center w-full sm:w-auto px-4 py-2.5 rounded-lg font-medium border border-gray-300 bg-white hover:bg-gray-50 text-gray-700"
              title="Cancel and go back"
            >
              Cancel
            </Link>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

export default CreateCompany;
